/**
 * Before a "send once" Automation goes to an older list: check its
 * addresses (promised to AWS with SES production access).
 *
 * Shows how much of the audience is already verified and how much is not,
 * with roughly what checking the rest costs in EmailListVerify credits.
 * "Verify first" runs the free checks on everyone, then sends what passes
 * to EmailListVerify in bulk, with progress here. "Free checks only" is the
 * admin's override: it sends on spelling, typo, throwaway-inbox and domain
 * checks alone. The schedule only fires after one of the two, on its own.
 */
import { useEffect } from 'react';
import { Alert, Anchor, Button, Group, Loader, Modal, Progress, SimpleGrid, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useEmailVerifyJob, useScheduleCheck, useStartScheduleCheck, type EmailVerifyJob,
} from '../../api/queries';
import { formatNumber } from '../../theme';

function Count({ label, n, hint }: { label: string; n: number; hint?: string }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fz={20} fw={700}>{formatNumber(n)}</Text>
      {hint && <Text size="xs" c="dimmed">{hint}</Text>}
    </Stack>
  );
}

/** What a running job is doing, in words. */
export function jobStage(j: EmailVerifyJob): string {
  if (j.status === 'failed') return 'Verification stopped';
  if (j.status === 'finished') return 'Verified';
  switch (j.phase) {
    case 'free': return 'Running the free checks';
    case 'upload': return 'Sending addresses to EmailListVerify';
    case 'poll': return `EmailListVerify is checking ${formatNumber(j.to_verify)} addresses`;
    default: return 'Working';
  }
}

export function PreflightModal({
  workflowId, scheduleId, name, onClose,
}: { workflowId: string; scheduleId: string; name: string; onClose: () => void }) {
  const qc = useQueryClient();
  const pf = useScheduleCheck(workflowId, scheduleId);
  const verify = useStartScheduleCheck();
  const jobId = verify.data?.id ?? pf.data?.job?.id;
  const job = useEmailVerifyJob(jobId);
  const j = job.data ?? pf.data?.job ?? null;
  const running = j?.status === 'running';
  const p = pf.data;
  const ready = !running && (p?.verify_status === 'done' || p?.verify_status === 'override' || j?.status === 'finished');

  // When a job finishes, count again: the numbers above are now verified.
  useEffect(() => {
    if (j && j.status !== 'running') {
      void qc.invalidateQueries({ queryKey: ['schedule-check', workflowId, scheduleId] });
      void qc.invalidateQueries({ queryKey: ['workflow-enrolments', workflowId] });
      void qc.invalidateQueries({ queryKey: ['workflows'] });
    }
  }, [j?.status, j, qc, workflowId, scheduleId]);

  function start(mode: 'elv' | 'free_only') {
    verify.mutate({ workflowId, scheduleId, mode }, {
      onError: (e) => notifications.show({ color: 'red', title: 'Not started', message: (e as Error).message }),
    });
  }

  const credits = p?.credits_available;
  const short = p && credits != null && credits < p.credits_needed;

  return (
    <Modal opened onClose={onClose} title={`Check the addresses: ${name}`} size="lg">
      <Stack data-testid="preflight">
        <Text size="sm" c="dimmed">
          Older addresses go stale: mailboxes close and domains lapse, and every bounce counts against
          you with Amazon. Check them before sending.
        </Text>
        {pf.isPending && <Group justify="center" py="md"><Loader size="sm" /></Group>}
        {pf.error && <Alert color="red" variant="light">{pf.error.message}</Alert>}
        {p && (
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            <Count label="In the audience" n={p.audience} />
            <Count label="Already verified" n={p.fresh} hint={p.fresh_blocked ? `${formatNumber(p.fresh_blocked)} won't be sent` : undefined} />
            <Count label="Never email" n={p.suppressed} />
            <Count label="Need checking" n={p.needs_verification} />
          </SimpleGrid>
        )}

        {j && (
          <Stack gap={6} data-testid="verify-progress">
            <Group justify="space-between">
              <Text size="sm" fw={600}>{jobStage(j)}</Text>
              <Text size="sm" c="dimmed">{j.status === 'finished' ? 100 : j.progress}%</Text>
            </Group>
            <Progress value={j.status === 'finished' ? 100 : Math.max(j.progress, running ? 4 : 0)}
              animated={running} color={j.status === 'failed' ? 'red' : 'teal'} />
            <Text size="xs" c="dimmed">
              {formatNumber(j.free_failed)} failed the free checks
              {j.mode === 'elv' ? `, ${formatNumber(j.verified)} of ${formatNumber(j.to_verify)} checked by EmailListVerify (${formatNumber(j.invalid)} invalid)` : ''}.
              {' '}Invalid addresses go on the never-email list.
            </Text>
            {j.error && <Alert color="red" variant="light"><Text size="sm">{j.error}</Text></Alert>}
          </Stack>
        )}

        {p && !running && !ready && (
          <Stack gap="xs">
            {p.elv_configured ? (
              <Text size="sm">
                About <b>{formatNumber(p.credits_needed)}</b> credits, one an address that passes the free checks.
                {credits != null && <> You have <b>{formatNumber(credits)}</b>.</>}
                {p.credits_error && <Text span c="red"> {p.credits_error}</Text>}
              </Text>
            ) : (
              <Alert variant="light" color="gray">
                <Text size="sm">
                  EmailListVerify is not set up, so only the free checks can run. Add a key in{' '}
                  <Anchor component={Link} to="/settings?tab=organisation" size="sm">Settings, under Services</Anchor> to check mailboxes too.
                </Text>
              </Alert>
            )}
            {short && (
              <Alert variant="light" color="chartAmber">
                <Text size="sm">Not enough credits for all of them. Top up first, or use the free checks only.</Text>
              </Alert>
            )}
          </Stack>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>{running ? 'Close, keep checking' : 'Cancel'}</Button>
          {p && !running && !ready && (
            <>
              <Button variant="default" loading={verify.isPending && verify.variables?.mode === 'free_only'}
                onClick={() => start('free_only')}>
                Free checks only
              </Button>
              <Button loading={verify.isPending && verify.variables?.mode === 'elv'}
                disabled={!p.elv_configured || !!short}
                onClick={() => start('elv')}>
                Verify {formatNumber(p.needs_verification)} addresses first (about {formatNumber(p.credits_needed)} credits)
              </Button>
            </>
          )}
          {ready && (
            <Button onClick={onClose}>Done, it sends at its time</Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
